import { DashboardLayout } from '@modules/dashboard/components/DashboardContents/DashboardLayout';
import { DASHBOARD_TV_MODE_POLL_DURATION } from '@modules/dashboard/constants';
import { getSavedTimeRange } from '@modules/dashboard/helpers';
import {
  useAddCollaboratorFromURL,
  useGetHasWhereClauseChanged,
  useIsDashboardEditable,
} from '@modules/dashboard/hooks';
import { useGetDashboardFromURL } from '@modules/dashboard/hooks/useGetDashboardFromURL';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Error404 from 'v2/views/components/error/error404';

export const DashboardContents = () => {
  const [searchParams] = useSearchParams();
  const query = useDashboardStore(({ query }) => query);
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const { loading, startPolling, stopPolling } = useGetDashboardFromURL();
  const hasWhereClauseChanged = useGetHasWhereClauseChanged({
    whereClause: query.whereClause,
    filterClause: dashboard?.filterClause,
  });

  const savedTimeRange = getSavedTimeRange({
    timeRange: dashboard?.filterClause?.timeRange,
  });

  const hasFiltersToBeSaved =
    !isEqual(query.duration, savedTimeRange) || hasWhereClauseChanged;

  const canEditDashboard = useIsDashboardEditable();

  useAddCollaboratorFromURL();

  useEffect(() => {
    const isDashboardInTVMode = searchParams.has('tv');

    stopPolling();
    if (isDashboardInTVMode) {
      startPolling(DASHBOARD_TV_MODE_POLL_DURATION);
    }
  }, [searchParams, startPolling, stopPolling]);

  useEffect(() => {
    if (!canEditDashboard) {
      return;
    }

    const warnBeforeQuitting = (event: BeforeUnloadEvent) => {
      if (hasFiltersToBeSaved) {
        event.preventDefault();
        event.returnValue = true;
      }
    };

    window.addEventListener('beforeunload', warnBeforeQuitting);

    return () => {
      window.removeEventListener('beforeunload', warnBeforeQuitting);
    };
  }, [hasFiltersToBeSaved, canEditDashboard]);

  if (!dashboard.id && !loading) {
    return <Error404 />;
  }

  /* Loading is when the dashboard in store has the
      default dashboard value and not current dashboard data */
  return <DashboardLayout isLoading={dashboard.id ? false : loading} />;
};
